import { create } from "zustand";
import { persist } from "zustand/middleware";

export const SIDEBAR_WIDTH = 244;
export const SIDEBAR_WIDTH_ICON = 60;

interface SidebarState {
  open: boolean;
  isHovered: boolean;
  autoCollapsed: boolean;
  openMobile: boolean;

  setOpen: (open: boolean) => void;
  setIsHovered: (hovered: boolean) => void;
  setAutoCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setOpenMobile: (open: boolean) => void;
}

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set, get) => ({
      open: true,
      isHovered: false,
      autoCollapsed: false,
      openMobile: false,

      setOpen: (open) => set({ open }),
      setIsHovered: (isHovered) => set({ isHovered }),
      setAutoCollapsed: (autoCollapsed) => set({ autoCollapsed }),
      setOpenMobile: (openMobile) => set({ openMobile }),

      toggleSidebar: () => {
        const { autoCollapsed } = get();
        set({ isHovered: false });
        if (autoCollapsed) {
          set({ autoCollapsed: false, open: true });
        } else {
          set((s) => ({ open: !s.open }));
        }
      },
    }),
    {
      name: "sidebar_open",
      partialize: (s) => ({ open: s.open }),
    },
  ),
);

export function useSidebar() {
  const store = useSidebarStore();
  const effectiveOpen = store.autoCollapsed ? false : store.open;
  const expanded = effectiveOpen || store.isHovered;
  return {
    ...store,
    state: (effectiveOpen ? "expanded" : "collapsed") as
      | "expanded"
      | "collapsed",
    open: effectiveOpen,
    expanded,
    gapWidth: expanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON,
    sidebarWidth: expanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON,
    openMobile: store.openMobile,
    setOpenMobile: store.setOpenMobile,
  };
}

export function useSidebarExpanded() {
  const open = useSidebarStore((s) => s.open);
  const isHovered = useSidebarStore((s) => s.isHovered);
  const autoCollapsed = useSidebarStore((s) => s.autoCollapsed);
  const effectiveOpen = autoCollapsed ? false : open;
  return effectiveOpen || isHovered;
}
